/**
 * ResultRow — one research result, shaped as a PubMed-style citation block (86d3qa4fq).
 *
 * A table row cannot carry this: at pane width every column ellipsises to uselessness, and a
 * citation is a shape (title → attribution → identifiers → snippet), not a set of fields. So
 * both result panes render this instead of DataTable columns, which keeps them reading as one
 * system even though their metadata differs.
 *
 * Presentational and source-agnostic: callers pass an already-resolved meta line so this stays
 * ignorant of monographs vs papers. All colour via --mg-* tokens.
 */

import React from 'react';
import { Typography } from 'antd';

const { Text } = Typography;

export interface ResultRowProps {
	title: string;
	/** Attribution line — authors for a paper, the source detail for a knowledge record. */
	attribution?: string | null;
	/** Identifier/provenance line — "J Physiol · 2023 · PMID 12345678". */
	meta?: string | null;
	/** Abstract or summary extract. Clamped to two lines — this is a scanning surface. */
	snippet?: string | null;
	/** Leading marker, e.g. a source StatusTag in the knowledge pane. */
	tag?: React.ReactNode;
	/** The record currently open in the drawer — keeps the reader's place once it closes. */
	selected?: boolean;
	onClick: () => void;
	'data-testid'?: string;
}

const ResultRow: React.FC<ResultRowProps> = ({
	title,
	attribution,
	meta,
	snippet,
	tag,
	selected = false,
	onClick,
	'data-testid': dataTestId,
}) => (
	<div
		role="button"
		tabIndex={0}
		aria-current={selected || undefined}
		onClick={onClick}
		onKeyDown={(e) => {
			// A div is only a button if it answers to the keyboard like one.
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				onClick();
			}
		}}
		className="mg-research-result-row"
		data-testid={dataTestId}
		style={{
			display: 'flex',
			flexDirection: 'column',
			gap: 2,
			padding: 'var(--mg-sp-4) var(--mg-sp-5)',
			borderBottom: '1px solid var(--mg-border)',
			cursor: 'pointer',
		}}
	>
		<div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
			{tag}
			<Text
				strong
				style={{ fontSize: 'var(--mg-fs-md)', color: 'var(--mg-primary)', flex: 1, minWidth: 0 }}
			>
				{title}
			</Text>
		</div>

		{attribution && (
			<Text ellipsis type="secondary" style={{ fontSize: 'var(--mg-fs-xs)' }}>
				{attribution}
			</Text>
		)}

		{meta && (
			<Text ellipsis style={{ fontSize: 'var(--mg-fs-xs)', color: 'var(--mg-text-secondary)' }}>
				{meta}
			</Text>
		)}

		{snippet && (
			<Text
				type="secondary"
				style={{
					fontSize: 'var(--mg-fs-xs)',
					// Two lines: enough to judge relevance, never enough to bury the next result.
					display: '-webkit-box',
					WebkitLineClamp: 2,
					WebkitBoxOrient: 'vertical',
					overflow: 'hidden',
				}}
			>
				{snippet}
			</Text>
		)}
	</div>
);

export default ResultRow;
